/**
 * Fuzzy name resolution for import link fields (development strategies, sections).
 * Uploaded files reference these by name while the plan stores internal IDs,
 * so small typos must still resolve to the intended record.
 */

/**
 * Normalises a name for comparison: trim, lowercase, collapse whitespace.
 *
 * @param {*} value Raw cell value
 * @returns {string}
 */
export const normalizeName = (value) => {
  return String(value ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
};

/**
 * Levenshtein edit distance (two-row dynamic programming).
 *
 * @param {string} a
 * @param {string} b
 * @returns {number}
 */
export const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let previous = Array.from({ length: b.length + 1 }, (_, i) => i);

  for (let i = 1; i <= a.length; i++) {
    const current = [i];

    for (let j = 1; j <= b.length; j++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;

      current[j] = Math.min(
        previous[j] + 1,
        current[j - 1] + 1,
        previous[j - 1] + substitutionCost,
      );
    }

    previous = current;
  }

  return previous[b.length];
};

/**
 * Resolves a raw name against a list of candidates.
 *
 * A unique exact match (after normalisation) wins outright. Otherwise the
 * nearest candidate within a length-relative threshold (~80% similarity,
 * with a floor of one edit for short names) is taken; a tie for nearest is
 * reported as ambiguous rather than guessing.
 *
 * @param {string} rawName Name as it appears in the uploaded file
 * @param {Array<{ id: number, name: string }>} candidates Existing records to match against
 * @returns {{ status: 'matched', candidate: object }
 *   | { status: 'ambiguous', candidates: object[] }
 *   | { status: 'unmatched', bestGuess: object|null }}
 */
export const resolveByName = (rawName, candidates) => {
  const input = normalizeName(rawName);

  const exactMatches = candidates.filter(c => normalizeName(c.name) === input);

  if (exactMatches.length === 1) return { status: 'matched', candidate: exactMatches[0] };
  if (exactMatches.length > 1) return { status: 'ambiguous', candidates: exactMatches };

  let bestGuess = null;
  let bestGuessDistance = Infinity;
  const accepted = [];

  candidates.forEach(candidate => {
    const name = normalizeName(candidate.name);
    const distance = levenshtein(input, name);
    const threshold = Math.max(1, Math.floor(0.2 * Math.max(input.length, name.length)));

    if (distance < bestGuessDistance) {
      bestGuess = candidate;
      bestGuessDistance = distance;
    }

    if (distance <= threshold) {
      accepted.push({ candidate, distance });
    }
  });

  if (accepted.length === 0) return { status: 'unmatched', bestGuess };

  const minDistance = Math.min(...accepted.map(a => a.distance));
  const nearest = accepted.filter(a => a.distance === minDistance);

  if (nearest.length > 1) {
    return { status: 'ambiguous', candidates: nearest.map(a => a.candidate) };
  }

  return { status: 'matched', candidate: nearest[0].candidate };
};
